import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  ArrowLeft, Crown, Check, Sparkles, FileEdit, LayoutTemplate, 
  Target, Download, RefreshCw, Loader2, Shield
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FEATURES = [
  { icon: FileEdit, text: 'Full Resume Refinement' },
  { icon: LayoutTemplate, text: 'All 29 Professional Templates' },
  { icon: Target, text: 'Advanced ATS Optimization' },
  { icon: Download, text: 'Downloads Without Watermark' },
  { icon: RefreshCw, text: 'Unlimited Downloads' },
  { icon: Sparkles, text: 'Priority AI Processing' },
];

// Load Razorpay script dynamically
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

export const UpgradePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, checkAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [actualPremiumStatus, setActualPremiumStatus] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(true);

  // Get the return path from state or sessionStorage
  const returnPath = location.state?.returnPath || sessionStorage.getItem('upgradeReturnPath') || '/home';

  // Store return path when component mounts
  useEffect(() => {
    if (location.state?.returnPath) {
      sessionStorage.setItem('upgradeReturnPath', location.state.returnPath);
    }
  }, [location.state]);

  // Check ACTUAL premium status from backend (not stale user data)
  useEffect(() => {
    const checkActualPremiumStatus = async () => {
      try {
        // Get auth token from storage
        const token = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
        const headers = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await fetch(`${API}/users/premium-status`, {
          credentials: 'include',
          headers
        });
        if (response.ok) {
          const data = await response.json();
          setActualPremiumStatus(data);
        }
      } catch (error) {
        console.error('Failed to check premium status:', error);
      } finally {
        setCheckingStatus(false);
      }
    };
    checkActualPremiumStatus();
  }, []);

  // Load Razorpay script on mount
  useEffect(() => {
    loadRazorpayScript().then((loaded) => {
      setRazorpayLoaded(loaded);
      if (!loaded) {
        console.error('Failed to load Razorpay SDK');
      }
    });
  }, []);

  const handleUpgrade = async (plan = 'premium') => {
    if (!razorpayLoaded) {
      toast.error('Payment service is loading. Please try again.');
      return;
    }

    setLoading(true);
    
    // Get download_id from session storage if available (from ConvertPage/CreateResumePage flow)
    const downloadId = sessionStorage.getItem('downloadId') || localStorage.getItem('downloadId');
    
    try {
      // Get auth token
      const token = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
      const authHeaders = { 'Content-Type': 'application/json' };
      if (token) {
        authHeaders['Authorization'] = `Bearer ${token}`;
      }
      
      // Step 1: Create order on backend with plan and download_id
      const orderResponse = await fetch(`${API}/users/create-order`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders,
        body: JSON.stringify({ plan: plan, download_id: downloadId })
      });

      if (!orderResponse.ok) {
        const error = await orderResponse.json();
        throw new Error(error.detail || 'Failed to create order');
      }

      const orderData = await orderResponse.json();

      // Store plan for verification
      sessionStorage.setItem('upgradePlan', plan);
      sessionStorage.setItem('upgradeReturnPath', returnPath);

      // Step 2: Open Razorpay checkout
      const options = {
        key: orderData.razorpay_key_id || orderData.key_id,
        amount: orderData.amount * 100, // in paise
        currency: orderData.currency,
        name: 'FixResume24',
        description: plan === 'single' ? 'Clean Download (No Watermark)' : 'Premium Access - 24 Hours',
        order_id: orderData.order_id,
        prefill: {
          name: orderData.user_name || '',
          email: orderData.user_email || '',
        },
        theme: {
          color: '#1e40af', // Primary blue
        },
        handler: function (response) {
          // Use synchronous handler, call async verification separately
          verifyUpgradePayment(
            response.razorpay_order_id,
            response.razorpay_payment_id,
            response.razorpay_signature
          );
        },
        modal: {
          ondismiss: function () {
            setLoading(false);
            toast.info('Payment cancelled. You can try again anytime.');
            // Redirect back to original page so user doesn't lose their work
            const savedReturnPath = sessionStorage.getItem('upgradeReturnPath') || '/convert';
            navigate(savedReturnPath);
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function (response) {
        toast.error(`Payment failed: ${response.error.description}`);
        setLoading(false);
        // Redirect back to original page so user doesn't lose their work
        const savedReturnPath = sessionStorage.getItem('upgradeReturnPath') || '/convert';
        navigate(savedReturnPath);
      });
      razorpay.open();

    } catch (error) {
      toast.error(error.message || 'Something went wrong');
      setLoading(false);
      // Redirect back to original page on error
      const savedReturnPath = sessionStorage.getItem('upgradeReturnPath') || '/convert';
      navigate(savedReturnPath);
    }
  };

  // Separate async function for payment verification
  const verifyUpgradePayment = async (orderId, paymentId, signature) => {
    try {
      const plan = sessionStorage.getItem('upgradePlan') || 'premium';
      const downloadId = sessionStorage.getItem('downloadId') || localStorage.getItem('downloadId');
      
      // Get auth token
      const token = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
      const authHeaders = { 'Content-Type': 'application/json' };
      if (token) {
        authHeaders['Authorization'] = `Bearer ${token}`;
      }
      
      const verifyResponse = await fetch(`${API}/users/verify-payment`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders,
        body: JSON.stringify({
          razorpay_order_id: orderId,
          razorpay_payment_id: paymentId,
          razorpay_signature: signature,
          plan: plan,
          download_id: downloadId
        }),
      });

      if (verifyResponse.ok) {
        const result = await verifyResponse.json();
        toast.success(result.message || 'Premium activated! Enjoy all features.');
        
        // CRITICAL: Wait for auth refresh to complete BEFORE redirecting
        await checkAuth();
        
        // Clean up session storage
        sessionStorage.removeItem('upgradeReturnPath');
        sessionStorage.removeItem('upgradePlan');
        
        // Set flag to tell ConvertPage that premium was just activated
        if (plan === 'premium') {
          sessionStorage.setItem('premiumJustActivated', 'true');
        }
        
        // If we have a download_id, redirect to download page
        const finalDownloadId = result.download_id || downloadId;
        if (finalDownloadId) {
          sessionStorage.removeItem('downloadId');
          localStorage.removeItem('downloadId');
          window.location.href = `/download?download_id=${finalDownloadId}`;
          return;
        }
        
        // Otherwise redirect to the return path
        const savedReturnPath = sessionStorage.getItem('upgradeReturnPath') || '/home';
        const baseUrl = window.location.origin;
        const fullRedirectUrl = `${baseUrl}${savedReturnPath}`;
        
        // Small delay to ensure state updates propagate, then hard redirect
        setTimeout(() => {
          window.location.replace(fullRedirectUrl);
        }, 500);
      } else {
        const error = await verifyResponse.json();
        toast.error(error.detail || 'Payment verification failed');
        setLoading(false);
      }
    } catch (err) {
      console.error('Payment verification error:', err);
      toast.error('Payment verification failed. Contact support if amount was deducted.');
      setLoading(false);
    }
  };

  // Show loading while checking actual premium status
  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Use ACTUAL premium status from backend, not stale user data
  const isActuallyPremium = actualPremiumStatus?.is_premium === true;

  if (isActuallyPremium) {
    // User is actually premium - redirect back to where they came from
    const savedReturnPath = sessionStorage.getItem('upgradeReturnPath') || '/home';
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-slate-50">
        <Header />
        <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Button 
            variant="ghost" 
            className="mb-6"
            onClick={() => navigate(savedReturnPath)}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>

          <Card className="border-slate-100">
            <CardContent className="p-12 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-6">
                <Crown className="h-10 w-10 text-green-600" />
              </div>
              <h1 className="font-heading text-3xl font-bold text-primary mb-2">
                You're Premium!
              </h1>
              <p className="text-muted-foreground mb-6">
                You have full access to all features. Continue with your resume.
              </p>
              <Button onClick={() => navigate(savedReturnPath)}>
                Continue
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-slate-50">
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          className="mb-6"
          onClick={() => navigate('/home')}
          data-testid="back-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Home
        </Button>

        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent/10 mb-4">
            <Crown className="h-8 w-8 text-accent" />
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-primary mb-4">
            Upgrade to Premium
          </h1>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto">
            Unlock the full power of FixResume24 with unlimited access to all features
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {/* Free Plan */}
          <Card className="border-slate-200">
            <CardHeader className="text-center pb-2">
              <CardTitle className="font-heading">Free</CardTitle>
              <p className="text-3xl font-bold text-primary mt-2">₹0</p>
              <p className="text-sm text-muted-foreground">Forever free</p>
            </CardHeader>
            <CardContent className="pt-4">
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2 text-muted-foreground">
                  <Check className="h-4 w-4 text-muted-foreground" />
                  11 Free Templates
                </li>
                <li className="flex items-center gap-2 text-muted-foreground">
                  <Check className="h-4 w-4 text-muted-foreground" />
                  Downloads with watermark
                </li>
                <li className="flex items-center gap-2 text-muted-foreground">
                  <Check className="h-4 w-4 text-muted-foreground" />
                  Basic AI Refinement
                </li>
              </ul>
              <Button 
                variant="outline" 
                className="w-full mt-6"
                disabled
              >
                Current Plan
              </Button>
            </CardContent>
          </Card>

          {/* ₹29 Single Download */}
          <Card className="border-slate-200">
            <CardHeader className="text-center pb-2">
              <CardTitle className="font-heading">Clean Download</CardTitle>
              <p className="text-3xl font-bold text-primary mt-2">₹29</p>
              <p className="text-sm text-muted-foreground">One-time</p>
            </CardHeader>
            <CardContent className="pt-4">
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  No watermark download
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  Current template only
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  PDF format
                </li>
              </ul>
              <Button 
                variant="outline"
                className="w-full mt-6"
                onClick={() => handleUpgrade('single')}
                disabled={loading || !razorpayLoaded}
                data-testid="upgrade-single-btn"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  'Pay ₹29'
                )}
              </Button>
            </CardContent>
          </Card>

          {/* ₹79 Premium Plan */}
          <Card className="border-2 border-primary relative overflow-hidden">
            <div className="absolute top-0 right-0 bg-accent text-white text-xs px-3 py-1 rounded-bl-lg font-medium">
              BEST VALUE
            </div>
            <CardHeader className="text-center pb-2">
              <CardTitle className="font-heading flex items-center justify-center gap-2">
                <Crown className="h-5 w-5 text-accent" />
                Premium
              </CardTitle>
              <p className="text-3xl font-bold text-primary mt-2">₹79</p>
              <p className="text-sm text-muted-foreground">24-hour access</p>
            </CardHeader>
            <CardContent className="pt-4">
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2 text-primary">
                  <Check className="h-4 w-4 text-green-500" />
                  All 29 Templates
                </li>
                <li className="flex items-center gap-2 text-primary">
                  <Check className="h-4 w-4 text-green-500" />
                  No watermark
                </li>
                <li className="flex items-center gap-2 text-primary">
                  <Check className="h-4 w-4 text-green-500" />
                  Full ATS Optimization
                </li>
                <li className="flex items-center gap-2 text-primary">
                  <Check className="h-4 w-4 text-green-500" />
                  Unlimited Downloads
                </li>
              </ul>
              <Button 
                className="w-full mt-6 bg-primary"
                onClick={() => handleUpgrade('premium')}
                disabled={loading || !razorpayLoaded}
                data-testid="upgrade-premium-btn"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <Crown className="mr-2 h-4 w-4" />
                    Pay ₹79
                  </>
                )}
              </Button>
              <div className="flex items-center justify-center gap-2 mt-3 text-xs text-muted-foreground">
                <Shield className="h-3 w-3" />
                Secure payment via Razorpay
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Trust Indicators */}
        <div className="mt-12 text-center space-y-2">
          <p className="text-sm text-muted-foreground">
            One-time payment • No auto-renewal • Instant access
          </p>
          <p className="text-xs text-muted-foreground">
            Payments processed securely by Razorpay. We never store your card details.
          </p>
        </div>
      </main>
    </div>
  );
};

export default UpgradePage;
